import React, { useEffect, useRef } from 'react';
import { animate, useInView, motion } from 'framer-motion';
import { Users, Briefcase, Timer, Shield } from 'lucide-react';

const Counter = ({ value, suffix = "" }: { value: number; suffix?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView && ref.current) {
      const controls = animate(0, value, {
        duration: 2,
        ease: "circOut",
        onUpdate(v) {
          if (ref.current) {
            // .toLocaleString() ayuda al SEO y legibilidad de números grandes
            ref.current.textContent = Math.floor(v).toLocaleString();
          }
        },
      });
      return () => controls.stop();
    }
  }, [isInView, value]);

  return (
    <span className="font-display font-extrabold" aria-live="polite">
      <span ref={ref}>0</span>
      {suffix}
    </span>
  );
};

const Stats: React.FC = () => {
  // Datos centralizados para fácil mantenimiento
  const stats = [
    { value: 80, suffix: 'k', label: 'Usuarios Protegidos', icon: Users },
    { value: 20, suffix: '+', label: 'Años de Experiencia', icon: Timer },
    { value: 250, suffix: '+', label: 'Proyectos Ejecutados', icon: Briefcase },
    { value: 100, suffix: '%', label: 'Continuidad Operativa', icon: Shield },
  ];

  return (
    <section 
      id="stats" 
      className="relative z-20 bg-primary -mt-32 pt-[2rem] pb-0"
      aria-label="Estadísticas de rendimiento y experiencia"
    >
      {/* SELLO DE UNIÓN CON HERO - Decorativo: aria-hidden */}
      <div className="absolute -top-[2px] left-0 w-full h-[4px] bg-surface z-[1]" aria-hidden="true" />

      {/* FONDO DIVIDIDO CON TEXTURA TÉCNICA */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        {/* Mitad Superior (Gris para el Hero) */}
        <div className="h-[200px] w-full bg-surface relative">
          <div
            className="absolute inset-0 opacity-[0.09]"
            style={{
              backgroundImage: `linear-gradient(#00647A 1px, transparent 1px), linear-gradient(90deg, #00647A 1px, transparent 1px)`,
              backgroundSize: '30px 30px',
              maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 90%)',
              WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 90%)'
            }}
          />
        </div>

        {/* Mitad Inferior (Azul Stellionet) */}
        <div className="absolute top-[200px] bottom-0 w-full bg-primary">
          <div className="absolute" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="bg-white rounded-[2rem] shadow-[0_-25px_80px_-20px_rgba(0,61,74,0.35),0_40px_80px_-20px_rgba(0,0,0,0.2)] p-8 md:p-12 border-2 border-white relative"
        >
          {/* Grid de estadísticas optimizado para responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {stats.map((stat, index) => (
              <article 
                key={index} 
                className="flex flex-col items-center text-center group relative"
              >
                {/* Contenedor de Icono con mejora visual y accesibilidad */}
                <div 
                  className="w-16 h-16 bg-secondary/10 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300"
                  aria-hidden="true"
                >
                  <stat.icon className="w-8 h-8 text-secondary" />
                </div>

                {/* Valor Numérico*/}
                <div className="text-4xl md:text-5xl font-extrabold mb-2 text-primary tracking-tight font-display">
                  <Counter value={stat.value} suffix={stat.suffix} />
                </div>

                {/* Etiqueta descriptiva: Mejora de contraste y semántica */}
                <p className="text-slate-600 font-bold uppercase text-xs tracking-widest">
                  {stat.label}
                </p>

                {/* Divisor Visual - Solo Desktop (Decorativo) */}
                {index < stats.length - 1 && (
                  <div 
                    className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 h-12 w-px bg-slate-100" 
                    aria-hidden="true" 
                  />
                )}
              </article>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Espaciador y SVG de transición final */}
      <div className="h-20 lg:h-32" />
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-10">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-[80px] fill-surface"
          shapeRendering="geometricPrecision"
          aria-hidden="true"
        >
          <path d="M0,120 L400,0 L1200,120 V125 H0 Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Stats;